/* eslint-disable react/no-unescaped-entities */
/**
 * Changelog — running log of platform changes surfaced to end users.
 * Pure data file so editing requires no JSX or new wiring; just add an
 * entry to the top of CHANGELOG and ship.
 *
 * Each entry: { date, version?, tag, title, items[{type, text, link?, adminOnly?}] }
 *   type      ∈ 'feature' | 'improvement' | 'fix'
 *   adminOnly: true → only rendered when viewing user is_admin (hidden from
 *              regular users). Use for platform-owner facing changes like
 *              feature flag admin UIs, admin-only dashboard fixes, internal
 *              API hardening, etc. Don't pollute the user changelog with
 *              things they can't see or take action on.
 */

export const CHANGELOG = [
  {
    date: '2026-06-27',
    version: '2.15',
    tag: 'Visual Creative Layer + 1-click campaigns',
    title: 'Brand Kits, AI Creative Studio, branded campaigns, and live engagement',
    items: [
      { type: 'feature', text: 'AI Creative Studio — generate branded, ready-to-post visuals: AI writes the on-image copy, your Brand Kit (logo + colors + fonts) is applied automatically, and you can drag the text, resize the headline, recolor, and adjust the overlay right on the canvas. Save straight to My Posts or download the PNG.', link: { to: '/studio/creative', label: 'Open Creative Studio' } },
      { type: 'feature', text: 'Brand Kits — save your logo, color palette and font once and reuse them across every creative.', link: { to: '/brand-kit', label: 'Manage Brand Kits' } },
      { type: 'feature', text: 'Branded campaign mode (Creative Studio) — enter one brief and auto-create a whole scheduled campaign where every AI image is brand-framed with the post\'s own headline, logo and colors. Captions & hashtags written for you.', link: { to: '/studio/creative', label: 'Try Branded campaign' } },
      { type: 'feature', text: '"Launch full campaign" — one click turns a previewed plan into a fully-wired funnel: schedule the posts, generate (optionally brand-framed) images, and draft a matching email newsletter + nurture sequence.' },
      { type: 'feature', text: 'New "Sent" tab in My Posts — see your published posts with a direct "View on platform" link, and pull live likes / comments straight from Facebook, Instagram and LinkedIn.', link: { to: '/posts?tab=sent', label: 'Open Sent' } },
      { type: 'improvement', text: 'AI Post Manager brief now has a live character counter with a 2,000-character cap so prompts stay within limits.' },
      { type: 'improvement', text: 'Weekly Themes can now generate AI images in the background (Nano Banana, included on every account) without timing out on long runs.' },
      { type: 'improvement', text: 'Show/hide password toggle on the login screen, and a mobile navigation menu on the landing page.' },
      { type: 'fix', text: 'Same-day campaigns are no longer scheduled in the past — posts always land in the future and stay "scheduled" instead of failing.' },
    ],
  },
  {
    date: '2026-06-05',
    version: '2.14',
    tag: 'Production stability + Meta v22',
    title: 'Facebook OAuth fixes, async campaign previews, Sent Reviews tracking',
    items: [
      { type: 'fix', text: 'Facebook "Feature Unavailable" — upgraded Graph API from deprecated v19.0 → v22.0 across 23 backend references.' },
      { type: 'fix', text: 'Multi-account Facebook connect → switched from deprecated auth_type=reauthenticate → rerequest (Meta v22+ supported).' },
      { type: 'fix', text: 'Stripe $1 promo trial — moved promo Price into Checkout line_items (the add_invoice_items parameter is not valid on Checkout Sessions).', adminOnly: true },
      { type: 'fix', text: 'AI Post Manager 504 timeouts — switched to background-job pattern (/campaigns/preview-async + polling).' },
      { type: 'fix', text: '/api/me/usage no longer 500s on edge-case users — wrapped in safe-default fallback.', adminOnly: true },
      { type: 'fix', text: 'Dialog accessibility — every modal now ships with aria-describedby (silences Radix warnings).', adminOnly: true },
      { type: 'feature', text: 'Sent Reviews tab inside My Posts — track every magic-link client review, with status filters, copy-link, cancel, and feedback display.', link: { to: '/posts?tab=reviews', label: 'Open Sent Reviews' } },
    ],
  },
  {
    date: '2026-06-03',
    version: '2.13',
    tag: 'Mobile responsive + Stripe signup gate',
    title: 'Mobile-friendly admin tabs and "must pay first" enforcement',
    items: [
      { type: 'fix', text: 'Edit Pricing Tier modal now scrolls properly on mobile — Save button no longer off-screen.', adminOnly: true },
      { type: 'improvement', text: 'Admin Dashboard tabs scroll horizontally on phones instead of overlapping.', adminOnly: true },
      { type: 'improvement', text: 'Weekly Themes day rows stack on mobile (1 row per day) — fits 390px screens cleanly.' },
      { type: 'improvement', text: 'Clients page header + "Manage another FB account" CTA stack on mobile.' },
      { type: 'feature', text: 'Signup-payment gate — users who click a paid plan on the pricing page MUST complete Stripe checkout before reaching the dashboard.', link: { to: '/subscription', label: 'View plans' } },
    ],
  },
  {
    date: '2026-06-02',
    version: '2.12',
    tag: 'Per-plan feature gating',
    title: 'Smarter upgrade prompts when a feature is locked',
    items: [
      { type: 'feature', text: 'New Admin → Features tab: toggle which subscription tiers can access each feature, instantly, no code changes.', link: { to: '/admin?tab=features', label: 'Admin → Features' }, adminOnly: true },
      { type: 'feature', text: '11 features catalogued across Agency, Content, Analytics categories (Weekly Themes, Magic-link Reviews, Style Profiles, Scan URL → Auto Post, AI Post Manager, CSV Export, etc.).', adminOnly: true },
      { type: 'feature', text: 'Hitting a feature locked to a higher plan now shows a clear "Upgrade" card with the plan list you need — not a generic error.' },
    ],
  },
  {
    date: '2026-06-01',
    version: '2.11',
    tag: 'Weekly Themes — 30+ day runs',
    title: 'Extended Weekly Themes runs and reusable templates',
    items: [
      { type: 'feature', text: 'Weekly Themes now supports up to 13 weeks (~90 days) per run. Pick a duration preset (1 week / 30 / 60 / 90 days) or custom.', link: { to: '/weekly-themes', label: 'Open Weekly Themes' } },
      { type: 'feature', text: 'Save any existing theme as a reusable template ("Save as Template" button on each card).' },
      { type: 'improvement', text: 'Built-in Starter Templates gallery (Real Estate, Restaurant, SaaS, etc.) — apply any with one click.' },
      { type: 'improvement', text: 'Generate dialog: live end-date label + posting-day estimate before you commit.' },
    ],
  },
  {
    date: '2026-05-28',
    version: '2.10',
    tag: 'Agency Phase C + Voice tools',
    title: 'Magic-link client reviews, Bulk CSV imports, Style profile previews',
    items: [
      { type: 'feature', text: 'Magic-link client review workflow — send any draft for client approval via a tokenized URL. Client approves/rejects without an account.' },
      { type: 'feature', text: 'Bulk Client Import (CSV) — upload up to 100 clients at once on /clients.', link: { to: '/clients', label: 'Open Clients' } },
      { type: 'feature', text: 'Weekly Themes voice preview — listen to how your tone sounds before generating the full run.' },
      { type: 'feature', text: '"Save preview as draft" — pull a sample post from the voice preview straight into drafts.' },
    ],
  },
  {
    date: '2026-05-20',
    version: '2.9',
    tag: 'Trial + URL scraping',
    title: '$1 / 5-day promo trial and Scan URL → Auto Post',
    items: [
      { type: 'feature', text: 'Try select paid plans for $1 / 5 days — full access during the trial, auto-converts to the regular monthly price after.', link: { to: '/subscription', label: 'View plans' } },
      { type: 'feature', text: 'Scan URL → Auto Post — paste any webpage and the system writes a captioned post from the scraped content.', link: { to: '/url-generator', label: 'Try URL Generator' } },
      { type: 'feature', text: 'Weekly Themed schedules — define a 7-day topic rotation, generate weeks of pre-scheduled posts at once.' },
      { type: 'improvement', text: 'Style Profiles integrated into Weekly Themes — per-day voice overrides.' },
    ],
  },
  {
    date: '2026-05-10',
    version: '2.8',
    tag: 'Durable media + SendGrid admin',
    title: 'AI images now survive redeploys',
    items: [
      { type: 'fix', text: 'Campaign-generated AI images no longer drop from scheduled FB/LinkedIn posts.' },
      { type: 'feature', text: 'Admin UI to manage SendGrid credentials dynamically (hot-swap without redeploy).', adminOnly: true },
      { type: 'improvement', text: 'Email verification reliability — clearer logging for bounced verification emails.', adminOnly: true },
    ],
  },
  {
    date: '2026-04-15',
    version: '2.7',
    tag: 'Style Profiles GA',
    title: 'Brand Voice / Style Profiles available across all generators',
    items: [
      { type: 'feature', text: 'Style Profiles — saved brand voice presets that lock caption output to a consistent voice.' },
      { type: 'feature', text: 'Apply Style Profiles in Compose, AI Post Manager, Campaign Manager, AI Blog, AI Email, and AI Ads.' },
    ],
  },
];

/* Group changelog by category for the new-feature badge logic */
export const getRecentChangelog = (limit = 5) => CHANGELOG.slice(0, limit);

export const LATEST_VERSION = CHANGELOG[0]?.version || '0.0';
export const LATEST_DATE = CHANGELOG[0]?.date || '2026-01-01';
